import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowLeft, FileText, Download, Edit, Trash2, 
  CheckCircle2, XCircle, Clock, Calendar, User, 
  Building2, DollarSign, Package, AlertTriangle,
  ChevronRight, Plus, Eye, Settings, Share2,
  Printer, Mail, MessageSquare, History, Star,
  Shield, Award, TrendingUp, BarChart2, Users,
  Phone, Mail as MailIcon, Globe, MapPin
} from 'lucide-react';

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data - replace with API call
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockRequest = {
        id: id,
        title: 'IT Equipment for New Office',
        requester: 'Ahmed Hassan',
        department: 'IT Department',
        priority: 'high',
        status: 'pending',
        category: 'Technology',
        totalAmount: 45000,
        currency: 'SAR',
        items: [
          { name: 'Laptops', quantity: 15, unitPrice: 2500, total: 37500, specifications: 'Dell Latitude 5520, 16GB RAM, 512GB SSD' },
          { name: 'Monitors', quantity: 15, unitPrice: 500, total: 7500, specifications: '24" Dell P2419H, Full HD' }
        ],
        submittedDate: '2024-03-15',
        requiredDate: '2024-03-25',
        description: 'New laptops and monitors for the expanding IT team. The equipment is needed to support the new office expansion and accommodate the growing team. This includes high-performance laptops for developers and quality monitors for better productivity.',
        attachments: ['specifications.pdf', 'budget_approval.docx', 'vendor_quotes.xlsx'],
        workflow: [
          { step: 'Submitted', user: 'Ahmed Hassan', date: '2024-03-15 09:30', status: 'completed', notes: 'Request submitted with all required documentation' },
          { step: 'Department Review', user: 'Sarah IT Manager', date: '2024-03-15 14:20', status: 'completed', notes: 'Reviewed and approved by IT department head' },
          { step: 'Budget Approval', user: 'Finance Team', date: '2024-03-16 11:15', status: 'completed', notes: 'Budget allocation confirmed' },
          { step: 'Procurement Review', user: 'Procurement Team', date: '2024-03-17 10:45', status: 'current', notes: 'Currently under procurement team review' },
          { step: 'Vendor Selection', user: '', date: '', status: 'pending', notes: '' },
          { step: 'PO Creation', user: '', date: '', status: 'pending', notes: '' },
          { step: 'Approved', user: '', date: '', status: 'pending', notes: '' }
        ],
        comments: [
          { user: 'Sarah IT Manager', date: '2024-03-15 14:25', text: 'Approved. Equipment specifications meet our requirements.' },
          { user: 'Finance Team', date: '2024-03-16 11:20', text: 'Budget approved. Funds allocated from IT equipment budget.' },
          { user: 'Ahmed Hassan', date: '2024-03-17 09:00', text: 'Urgent: Need equipment by end of month for new team onboarding.' }
        ],
        vendorQuotes: [
          { vendor: 'Tech Solutions Ltd', amount: 45000, rating: 4.5, delivery: '5-7 days', notes: 'Preferred vendor, good track record' },
          { vendor: 'Digital Equipment Co', amount: 47000, rating: 4.2, delivery: '7-10 days', notes: 'Slightly higher price but includes extended warranty' },
          { vendor: 'Office Supplies Pro', amount: 43000, rating: 4.0, delivery: '10-14 days', notes: 'Lowest price but longer delivery time' }
        ]
      };
      setRequest(mockRequest);
      setIsLoading(false);
    }, 500);
  }, [id]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'workflow', label: 'Workflow', icon: History },
    { id: 'items', label: 'Items', icon: Package },
    { id: 'quotes', label: 'Vendor Quotes', icon: Award },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'attachments', label: 'Attachments', icon: FileText }
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
      critical: 'bg-purple-100 text-purple-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      current: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getWorkflowStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-white" />;
      case 'current':
        return <Clock className="w-5 h-5 text-white" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-white" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = () => {
    setRequest(prev => ({
      ...prev,
      status: 'approved',
      workflow: prev.workflow.map(step => 
        step.step === 'Approved' 
          ? { ...step, status: 'completed', user: 'Current User', date: new Date().toLocaleString() }
          : step
      )
    }));
    setShowApproveModal(false);
  };

  const handleReject = () => {
    setRequest(prev => ({
      ...prev,
      status: 'rejected',
      workflow: prev.workflow.map(step => 
        step.step === 'Procurement Review' 
          ? { ...step, status: 'rejected', user: 'Current User', date: new Date().toLocaleString() }
          : step
      )
    }));
    setShowRejectModal(false);
  };

  const handleDelete = () => {
    navigate('/supply-chain/purchase-requests');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Not Found</h2>
          <p className="text-gray-600 mb-4">The request you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/supply-chain/purchase-requests')}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Back to Requests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/supply-chain/purchase-requests')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Purchase Request</h1>
                <p className="text-sm text-gray-500">Request ID: {request.id}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>
              {request.status === 'pending' && (
                <>
                  <button
                    onClick={() => setShowApproveModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                </>
              )}
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Request Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{request.title}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <span>Department: {request.department}</span>
                    <span>Category: {request.category}</span>
                    <span>Submitted: {formatDate(request.submittedDate)}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(request.priority)}`}>
                      {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)} Priority
                    </span>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                      {request.status === 'in-review' ? 'In Review' : 
                       request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                    <span className="text-2xl font-bold text-emerald-600">
                      {request.currency} {request.totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-white border-2 border-gray-200 rounded-lg flex items-center justify-center mb-2">
                    <QRCodeSVG value={`Request:${request.id}|${request.title}`} size={60} level="M" />
                  </div>
                  <span className="text-xs text-gray-500">Scan for details</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === tab.id
                            ? 'border-emerald-500 text-emerald-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                      <p className="text-gray-700 leading-relaxed">{request.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Request Information</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Requested By:</span>
                            <span className="font-medium">{request.requester}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Department:</span>
                            <span className="font-medium">{request.department}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Category:</span>
                            <span className="font-medium">{request.category}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Priority:</span>
                            <span className={`font-medium ${getPriorityColor(request.priority)}`}>
                              {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Timeline</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Submitted:</span>
                            <span className="font-medium">{formatDate(request.submittedDate)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Required By:</span>
                            <span className="font-medium">{formatDate(request.requiredDate)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className={`font-medium ${getStatusColor(request.status)}`}>
                              {request.status === 'in-review' ? 'In Review' : 
                               request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Value:</span>
                            <span className="font-bold text-emerald-600">
                              {request.currency} {request.totalAmount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'workflow' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval Workflow</h3>
                    <div className="relative">
                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      <div className="space-y-6">
                        {request.workflow.map((step, index) => (
                          <div key={index} className="relative flex items-start">
                            <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                              step.status === 'completed' ? 'bg-green-600 border-green-600' :
                              step.status === 'current' ? 'bg-blue-600 border-blue-600' :
                              step.status === 'rejected' ? 'bg-red-600 border-red-600' :
                              'bg-white border-gray-300'
                            }`}>
                              {getWorkflowStatusIcon(step.status)}
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-lg font-medium text-gray-900">{step.step}</h4>
                                {step.date && (
                                  <span className="text-sm text-gray-500">{step.date}</span>
                                )}
                              </div>
                              {step.user && (
                                <p className="text-sm text-gray-600 mb-2">by {step.user}</p>
                              )}
                              {step.notes && (
                                <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                                  {step.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'items' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Requested Items</h3>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                          <div className="col-span-5">Item Description</div>
                          <div className="col-span-2">Quantity</div>
                          <div className="col-span-2">Unit Cost</div>
                          <div className="col-span-2">Total Cost</div>
                          <div className="col-span-1">Specs</div>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {request.items.map((item, index) => (
                          <div key={index} className="px-6 py-4 hover:bg-gray-50">
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-5">
                                <p className="font-medium text-gray-900">{item.name}</p>
                                <p className="text-sm text-gray-500">{item.specifications}</p>
                              </div>
                              <div className="col-span-2 text-gray-900 font-medium">{item.quantity}</div>
                              <div className="col-span-2 text-gray-900">{request.currency} {item.unitPrice.toLocaleString()}</div>
                              <div className="col-span-2 text-gray-900 font-medium">{request.currency} {item.total.toLocaleString()}</div>
                              <div className="col-span-1">
                                <button className="p-1 hover:bg-gray-100 rounded">
                                  <FileText className="h-4 w-4 text-gray-400" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">Total Estimated Cost</span>
                          <span className="text-2xl font-bold text-emerald-600">{request.currency} {request.totalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'quotes' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendor Quotes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {request.vendorQuotes.map((quote, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900">{quote.vendor}</h4>
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 fill-current" />
                              <span className="ml-1 text-sm font-medium">{quote.rating}</span>
                            </div>
                          </div>
                          <div className="space-y-2 mb-4">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-semibold text-emerald-600">{request.currency} {quote.amount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Delivery:</span>
                              <span className="font-medium">{quote.delivery}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mb-4">{quote.notes}</p>
                          <div className="flex space-x-2">
                            <button className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm">
                              Select
                            </button>
                            <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Comments & Notes</h3>
                      <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Comment
                      </button>
                    </div>
                    <div className="space-y-4">
                      {request.comments.map((comment, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                              <span className="font-medium text-gray-900">{comment.user}</span>
                            </div>
                            <span className="text-sm text-gray-500">{formatDate(comment.date)}</span>
                          </div>
                          <p className="text-gray-700">{comment.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'attachments' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Attachments</h3>
                      <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Upload File
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {request.attachments.map((attachment, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <FileText className="h-5 w-5 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{attachment}</p>
                              <p className="text-xs text-gray-500">PDF • 2.3 MB</p>
                            </div>
                          </div>
                          <div className="flex space-x-2 mt-3">
                            <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                              Download
                            </button>
                            <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Approve Request
                </button>
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Request
                </button>
                <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Share Request
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Send Email
                </button>
              </div>
            </div>

            {/* Request Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Info</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">Request ID</span>
                  <p className="font-medium">{request.id}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                    {request.status === 'in-review' ? 'In Review' : 
                     request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Priority</span>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request.priority)}`}>
                    {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Total Value</span>
                  <p className="font-bold text-emerald-600">{request.currency} {request.totalAmount.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">Submitted</p>
                    <p className="text-xs text-gray-500">{formatDate(request.submittedDate)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">Department Review</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">Budget Approval</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="text-sm font-medium">Procurement Review</p>
                    <p className="text-xs text-gray-500">In Progress</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Approve Request</h3>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to approve this request? This action cannot be undone.
              </p>
            </div>
            <div className="p-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Reject Request</h3>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to reject this request? This action cannot be undone.
              </p>
            </div>
            <div className="p-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Request</h3>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this request? This action cannot be undone.
              </p>
            </div>
            <div className="p-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetail; 